import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'

const API_URL = process.env.REACT_APP_API_URL


function parseApiResponse(raw) {
  let d = raw
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d)
    } catch {
      return raw
    }
  }
  if (d?.body != null) {
    if (typeof d.body === 'string') {
      try {
        return JSON.parse(d.body)
      } catch {
        return d.body
      }
    }
    return d.body
  }
  return d
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function getMaterialGstPercent(row) {
  if (row?.gstPercent != null && row.gstPercent !== '') return row.gstPercent
  if (row?.gst != null && row.gst !== '') return row.gst
  return null
}

function formatMaterialMoney(value) {
  if (value == null || value === '') return '-'
  const n = parseFloat(value)
  return Number.isFinite(n) ? n.toFixed(2) : String(value)
}

function getMaterialGstValueDisplay(row) {
  if (row?.gstValue != null && row.gstValue !== '') {
    return formatMaterialMoney(row.gstValue)
  }
  const basic = parseFloat(row?.basicPrice) || 0
  const qty = parseFloat(row?.quantity) || 0
  const gst = parseFloat(getMaterialGstPercent(row)) || 0
  if (basic > 0 && qty > 0 && gst > 0) {
    return (basic * qty * (gst / 100)).toFixed(2)
  }
  return '-'
}

function getMaterialTotalValueDisplay(row) {
  if (row?.totalValue != null && row.totalValue !== '') {
    return formatMaterialMoney(row.totalValue)
  }
  const basic = parseFloat(row?.basicPrice) || 0
  const qty = parseFloat(row?.quantity) || 0
  const gst = parseFloat(getMaterialGstPercent(row)) || 0
  if (basic > 0 && qty > 0) {
    const totalBasic = basic * qty
    return (totalBasic + totalBasic * (gst / 100)).toFixed(2)
  }
  return '-'
}

function calcGrossTotalsFromMaterials(materialRows) {
  return materialRows.reduce(
    (acc, row) => {
      const lineBasic =
        (parseFloat(row?.basicPrice) || 0) * (parseFloat(row?.quantity) || 0)
      let gstValue = parseFloat(row?.gstValue)
      if (!Number.isFinite(gstValue)) {
        const gst = parseFloat(getMaterialGstPercent(row)) || 0
        gstValue = (lineBasic * gst) / 100
      }
      let totalValue = parseFloat(row?.totalValue)
      if (!Number.isFinite(totalValue)) totalValue = lineBasic + gstValue
      return {
        grossTotalBasicPrice: acc.grossTotalBasicPrice + lineBasic,
        grossGstValue: acc.grossGstValue + gstValue,
        grossTotalValue: acc.grossTotalValue + totalValue
      }
    },
    { grossTotalBasicPrice: 0, grossGstValue: 0, grossTotalValue: 0 }
  )
}

function formatDateParts(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date)
  const get = (type) => parts.find((p) => p.type === type)?.value || ''
  return `${get('day')}/${get('month')}/${get('year')}`
}

function formatDate(value) {
  if (!value) return '-'
  const str = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str.replace(/-/g, '/')
  const m = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T]/)
  if (m) return `${m[1]}/${m[2]}/${m[3]}`
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'
  return formatDateParts(d)
}

function formatDateTime(value) {
  if (!value) return '-'
  const str = String(value).trim()
  const m = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})/)
  if (m) {
    const [, dd, mm, yyyy, hh, min] = m
    const hour = parseInt(hh, 10)
    const period = hour >= 12 ? 'PM' : 'AM'
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${dd}/${mm}/${yyyy}, ${String(h12).padStart(2, '0')}:${min} ${period}`
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).formatToParts(d)
  const get = (type) => parts.find((p) => p.type === type)?.value || ''
  return `${get('day')}/${get('month')}/${get('year')}, ${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase()}`
}

function hasPoPdf(po) {
  return Boolean(String(po?.pdfDownloadUrl || po?.pdfLink || '').trim())
}

const ViewField = ({ label, children }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">{label}</label>
    <div className="col-12 col-md-8">
      <div className="txtdsply">{children}</div>
    </div>
  </div>
)

const SectionTitle = ({ icon, title }) => (
  <div className="row mb-2 mt-3">
    <div className="col-12">
      <h5 className="text-primary border-bottom pb-2 mb-3">
        <i className={`fa-solid ${icon} mr-2`} />
        {title}
      </h5>
    </div>
  </div>
)


function PoSupplierView() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [po, setPo] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isOpeningPdf, setIsOpeningPdf] = useState(false)
  const [downloadingS3Key, setDownloadingS3Key] = useState('')

  const fetchPo = useCallback(async () => {
    if (!id) {
      toast.error('PO ID is missing', { autoClose: 2000 })
      navigate('/poSupplierList')
      return
    }

    setIsLoading(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-supplier-po-details',
        currentUserId: localStorage.getItem('id') || undefined,
        poId: id
      })
      const responseData = parseApiResponse(response.data)

      if (isApiSuccess(responseData) && responseData?.data) {
        setPo(responseData.data)
      } else {
        toast.error(responseData?.message || 'Failed to fetch PO details', {
          autoClose: 2000
        })
        navigate('/poSupplierList')
      }
    } catch (error) {
      console.error('Fetch supplier PO error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while fetching PO details',
        { autoClose: 2000 }
      )
      navigate('/poSupplierList')
    } finally {
      setIsLoading(false)
    }
  }, [id, navigate])

  useEffect(() => {
    fetchPo()
  }, [fetchPo])

  const materials = useMemo(
    () => (Array.isArray(po?.materials) ? po.materials : []),
    [po]
  )
  const terms = useMemo(() => (Array.isArray(po?.terms) ? po.terms : []), [po])
  const owners = useMemo(
    () => (Array.isArray(po?.poOwner) ? po.poOwner : []),
    [po]
  )
  const materialGrossTotals = useMemo(
    () => calcGrossTotalsFromMaterials(materials),
    [materials]
  )

  const handleViewPdf = () => {
    const url = po?.pdfDownloadUrl || po?.pdfLink
    if (!url) {
      toast.error('PDF is not available for this PO', { autoClose: 2000 })
      return
    }
    setIsOpeningPdf(true)
    try {
      window.open(url, '_blank', 'noopener,noreferrer')
    } finally {
      setIsOpeningPdf(false)
    }
  }

  const downloadPoOwnerAttachment = async (att) => {
    const s3Key = att?.s3Key
    if (!s3Key || downloadingS3Key) return

    const directUrl = att.viewUrl || att.downloadUrl || att.url
    if (directUrl) {
      window.open(directUrl, '_blank', 'noopener,noreferrer')
      return
    }

    setDownloadingS3Key(s3Key)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-po-owner-attachment-download-presigned-url',
        s3Keys: [s3Key],
        expiresIn: 3600
      })
      const responseData = parseApiResponse(response.data)
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to get download URL', {
          autoClose: 2000
        })
        return
      }
      const downloadUrl =
        responseData.files?.[0]?.downloadUrl ||
        responseData.downloadUrls?.[0] ||
        responseData.downloadUrl
      if (!downloadUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Download PO owner attachment error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(errData?.message || 'Failed to download attachment', {
        autoClose: 2000
      })
    } finally {
      setDownloadingS3Key('')
    }
  }

  const header = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-6 col-sm-6">
            <h1 className="m-0 txt_lft">View Purchase Order</h1>
          </div>
          <div className="col-6 col-sm-6">
            <ol className="breadcrumb justify-content-end mb-0">
              <li className="breadcrumb-item">
                <Link to={'/poSupplierList'}>
                  <i className="fa-solid fa-arrow-left-long" /> Back
                </Link>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  )

  const wrapCard = (body) => (
    <div>
      {header}
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">{body}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  if (isLoading) {
    return wrapCard(
      <div className="card-body text-center py-5">
        <PulseLoader color="#1199B2" size={10} />
      </div>
    )
  }

  if (!po) {
    return wrapCard(
      <div className="card-body text-center py-5">
        <p>PO not found</p>
      </div>
    )
  }

  return wrapCard(
    <div className="card-body">
      {/* PO Details */}
      <SectionTitle icon="fa-file-lines" title="PO Details" />
      <ViewField label="PO Number">{po.poNumber || '-'}</ViewField>
      <ViewField label="PO Date">{formatDate(po.poDate)}</ViewField>
      <ViewField label="Delivery Date">{formatDate(po.poDeliveryDate)}</ViewField>
      <ViewField label="Currency">{po.poCurrency || '-'}</ViewField>
      <ViewField label="PO Status">
        <span className="fwt_600">{po.poStatus || '-'}</span>
      </ViewField>
      <ViewField label="PO Summary">
        <span style={{ whiteSpace: 'pre-wrap' }}>{po.poSummary || '-'}</span>
      </ViewField>

      {/* Terms */}
      <SectionTitle icon="fa-list-check" title="Terms" />
      {terms.length === 0 ? (
        <div className="txtdsply text-muted mb-3">No terms</div>
      ) : (
        <div className="table-responsive mb-3">
          <table className="table table-bordered table-sm mb-0">
            <thead>
              <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                <th style={{ fontSize: '12px', width: '48px' }}>S.No</th>
                <th style={{ fontSize: '12px', minWidth: '160px' }}>Term</th>
                <th style={{ fontSize: '12px', minWidth: '200px' }}>Conditions</th>
              </tr>
            </thead>
            <tbody>
              {terms.map((term, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {term.term || '-'}
                  </td>
                  <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {term.response || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Materials */}
      <SectionTitle icon="fa-boxes-stacked" title="Materials" />
      {materials.length === 0 ? (
        <div className="txtdsply text-muted mb-3">No materials</div>
      ) : (
        <div className="table-responsive mb-3">
          <table
            className="table table-bordered table-sm mb-0"
            style={{ minWidth: '1200px' }}
          >
            <thead>
              <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                <th style={{ fontSize: '12px' }}>S.No</th>
                <th style={{ fontSize: '12px' }}>Material Code</th>
                <th style={{ fontSize: '12px' }}>Material Description</th>
                <th style={{ fontSize: '12px' }}>Ref. Number</th>
                <th style={{ fontSize: '12px' }}>HSN</th>
                <th style={{ fontSize: '12px' }}>UOM</th>
                <th style={{ fontSize: '12px' }}>Quantity</th>
                <th style={{ fontSize: '12px' }}>Basic Price</th>
                <th style={{ fontSize: '12px' }}>GST %</th>
                <th style={{ fontSize: '12px' }}>GST Value</th>
                <th style={{ fontSize: '12px' }}>Total Value</th>
                <th style={{ fontSize: '12px' }}>Technical Remarks</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((row, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{row.materialCode || '-'}</td>
                  <td
                    style={{
                      maxWidth: '240px',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word'
                    }}
                  >
                    {row.materialDescription || '-'}
                  </td>
                  <td>{row.referenceNumber || row.prNumber || '-'}</td>
                  <td>{row.hsn || row.hsnCode || '-'}</td>
                  <td>{row.uom || '-'}</td>
                  <td>{row.quantity ?? '-'}</td>
                  <td>{formatMaterialMoney(row.basicPrice)}</td>
                  <td>
                    {getMaterialGstPercent(row) != null
                      ? `${getMaterialGstPercent(row)}%`
                      : '-'}
                  </td>
                  <td>{getMaterialGstValueDisplay(row)}</td>
                  <td>{getMaterialTotalValueDisplay(row)}</td>
                  <td
                    style={{
                      maxWidth: '200px',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word'
                    }}
                  >
                    {row.technicalRemarks || '-'}
                  </td>
                </tr>
              ))}
              <tr style={{ backgroundColor: '#f4f6f9' }}>
                <td
                  colSpan={7}
                  className="text-right align-middle"
                  style={{ fontSize: '13px', fontWeight: 600 }}
                >
                  Gross Total
                  {po.poCurrency ? ` (${po.poCurrency})` : ''}
                </td>
                <td className="align-middle" style={{ fontSize: '13px', fontWeight: 600 }}>
                  {formatMaterialMoney(materialGrossTotals.grossTotalBasicPrice)}
                </td>
                <td />
                <td className="align-middle" style={{ fontSize: '13px', fontWeight: 600 }}>
                  {formatMaterialMoney(materialGrossTotals.grossGstValue)}
                </td>
                <td className="align-middle" style={{ fontSize: '13px', fontWeight: 600 }}>
                  {formatMaterialMoney(materialGrossTotals.grossTotalValue)}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}


      {/* PO Owner Attachments */}
      <SectionTitle icon="fa-paperclip" title="PO Owner Attachments" />
      {owners.length === 0 ? (
        <div className="txtdsply text-muted mb-3">No attachments</div>
      ) : (
        <div className="table-responsive mb-3">
          <table className="table table-bordered table-sm mb-0">
            <thead>
              <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                <th style={{ fontSize: '12px' }}>S.No</th>
                <th style={{ fontSize: '12px' }}>File Name</th>
                <th style={{ fontSize: '12px' }}>Uploaded At</th>
                <th style={{ fontSize: '12px', width: '90px' }}>View</th>
              </tr>
            </thead>
            <tbody>
              {owners.map((att, index) => {
                const s3Key = att.s3Key || ''
                const isDownloading = downloadingS3Key === s3Key
                return (
                  <tr key={s3Key || index}>
                    <td>{index + 1}</td>
                    <td>{att.originalFileName || att.fileName || s3Key || '-'}</td>
                    <td>{formatDateTime(att.uploadedAt)}</td>
                    <td className="text-center">
                      {s3Key && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          title="Open attachment"
                          onClick={() => downloadPoOwnerAttachment(att)}
                          disabled={isDownloading || Boolean(downloadingS3Key)}
                        >
                          {isDownloading ? (
                            <i className="fa-solid fa-spinner fa-spin" />
                          ) : (
                            <i className="fa-solid fa-up-right-from-square" />
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

            {/* Document */}
      <SectionTitle icon="fa-file-pdf" title="Document" />
      <ViewField label="PDF Link">
        {hasPoPdf(po) ? (
          <button
            type="button"
            className="btn btn-link p-0 align-baseline"
            onClick={handleViewPdf}
            disabled={isOpeningPdf}
          >
            {isOpeningPdf ? (
              <>
                <i className="fa-solid fa-spinner fa-spin mr-1" />
                Opening...
              </>
            ) : (
              'View PDF'
            )}
          </button>
        ) : (
          '-'
        )}
      </ViewField>
    </div>
  )
}

export default PoSupplierView